import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  Alert
} from '@mui/material';
import {
  Upload,
  Folder,
  AudioFile,
  Home,
  CloudUpload,
  FolderOpen
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';

/**
 * AudioPathSelector Component
 *
 * Un componente que permite seleccionar o subir archivos de audio
 *
 * @param {Object} props
 * @param {string} props.value - Valor actual del path del audio (ej: "enigma/audio1")
 * @param {Function} props.onChange - Callback cuando cambia el valor
 * @param {string} props.label - Label del TextField
 * @param {string} props.placeholder - Placeholder del TextField
 * @param {string} props.helperText - Texto de ayuda
 * @param {Object} props.textFieldProps - Props adicionales para el TextField
 */
export default function AudioPathSelector({
  value = '',
  onChange,
  label = 'Ruta del audio',
  placeholder = 'enigma/audio1',
  helperText = 'Path del archivo de audio sin extensión',
  textFieldProps = {}
}) {
  const { multimedia } = useApi();

  // Estados del diálogo
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0); // 0: Seleccionar, 1: Subir
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', severity: 'info' });

  // Estados para navegación de archivos
  const [currentPath, setCurrentPath] = useState('');
  const [directories, setDirectories] = useState([]);
  const [files, setFiles] = useState([]);

  // Estados para upload
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const showNotification = (message, severity = 'info') => {
    setNotification({ show: true, message, severity });
  };

  const handleOpen = () => {
    setOpen(true);
    if (tab === 0) {
      loadDirectory('');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setUploadFile(null);
    setCurrentPath('');
    setNotification({ show: false, message: '', severity: 'info' });
  };

  const loadDirectory = async (path) => {
    try {
      setLoading(true);
      const response = await multimedia.browse('sounds', path);
      setDirectories(response.directories || []);
      setFiles(response.files || []);
      setCurrentPath(path);
    } catch (error) {
      showNotification('Error cargando directorio: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const navigateToPath = (newPath) => {
    loadDirectory(newPath);
  };

  const navigateUp = () => {
    if (currentPath) {
      const parentPath = currentPath.split('/').slice(0, -1).join('/');
      loadDirectory(parentPath);
    }
  };

  const getBreadcrumbs = () => {
    const parts = currentPath ? currentPath.split('/').filter(Boolean) : [];
    const breadcrumbs = [{ label: 'Audios', path: '' }];

    let buildPath = '';
    parts.forEach(part => {
      buildPath += (buildPath ? '/' : '') + part;
      breadcrumbs.push({ label: part, path: buildPath });
    });

    return breadcrumbs;
  };

  const handleFileSelect = (file) => {
    // Remover la extensión del nombre del archivo
    const fileNameWithoutExt = file.name.replace(/\.(wav|mp3|gsm|ulaw|alaw|g722|g729|ogg)$/i, '');
    const fullPath = currentPath ? `${currentPath}/${fileNameWithoutExt}` : fileNameWithoutExt;

    onChange(fullPath);
    handleClose();
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      showNotification('Selecciona un archivo para subir', 'warning');
      return;
    }

    try {
      setUploading(true);
      await multimedia.upload('sounds', currentPath, uploadFile);

      // Remover la extensión del nombre del archivo
      const fileNameWithoutExt = uploadFile.name.replace(/\.(wav|mp3|gsm|ulaw|alaw|g722|g729|ogg)$/i, '');
      const fullPath = currentPath ? `${currentPath}/${fileNameWithoutExt}` : fileNameWithoutExt;

      onChange(fullPath);
      showNotification('Archivo subido correctamente', 'success');

      // Esperar un momento antes de cerrar para que el usuario vea el mensaje
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      showNotification('Error subiendo archivo: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        helperText={helperText}
        fullWidth
        InputProps={{
          endAdornment: (
            <Tooltip title="Seleccionar o subir audio">
              <IconButton onClick={handleOpen} edge="end">
                <FolderOpen />
              </IconButton>
            </Tooltip>
          )
        }}
        {...textFieldProps}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            minHeight: '450px',
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
          color: 'white',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AudioFile sx={{ mr: 1 }} />
            Seleccionar Audio
          </Box>
        </DialogTitle>

        <Tabs
          value={tab}
          onChange={(e, newTab) => {
            setTab(newTab);
            if (newTab === 0 && directories.length === 0 && files.length === 0) {
              loadDirectory('');
            }
          }}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Seleccionar Existente" />
          <Tab label="Subir Nuevo" />
        </Tabs>

        <DialogContent sx={{ mt: 2, minHeight: 350 }}>
          {notification.show && (
            <Alert severity={notification.severity} sx={{ mb: 2 }} onClose={() => setNotification({ ...notification, show: false })}>
              {notification.message}
            </Alert>
          )}

          {tab === 0 && (
            <Box>
              {/* Breadcrumbs */}
              <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Breadcrumbs>
                  {getBreadcrumbs().map((crumb, index) => (
                    <Link
                      key={index}
                      component="button"
                      underline="hover"
                      onClick={() => navigateToPath(crumb.path)}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {index === 0 && <Home sx={{ mr: 0.5 }} fontSize="inherit" />}
                      {crumb.label}
                    </Link>
                  ))}
                </Breadcrumbs>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {/* Parent directory link */}
                  {currentPath && (
                    <ListItem disablePadding>
                      <ListItemButton onClick={navigateUp}>
                        <ListItemIcon>
                          <Folder />
                        </ListItemIcon>
                        <ListItemText primary=".." secondary="Directorio padre" />
                      </ListItemButton>
                    </ListItem>
                  )}

                  {/* Directories */}
                  {directories.map((dir, index) => (
                    <ListItem key={`dir-${index}`} disablePadding>
                      <ListItemButton onClick={() => navigateToPath(currentPath ? `${currentPath}/${dir.name}` : dir.name)}>
                        <ListItemIcon>
                          <Folder color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={dir.name} secondary="Carpeta" />
                      </ListItemButton>
                    </ListItem>
                  ))}

                  {/* Files */}
                  {files.map((file, index) => (
                    <ListItem key={`file-${index}`} disablePadding>
                      <ListItemButton onClick={() => handleFileSelect(file)}>
                        <ListItemIcon>
                          <AudioFile color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${file.extension} • ${(file.size / 1024).toFixed(2)} KB`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}

                  {directories.length === 0 && files.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="textSecondary">
                        No hay archivos o carpetas en este directorio
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ py: 2 }}>
              {uploading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="body1">Subiendo archivo...</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Por favor espera...
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Directorio actual */}
                  <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Subir a:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      /var/lib/asterisk/sounds/{currentPath || '(raíz)'}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Folder />}
                      onClick={() => {
                        setTab(0);
                        loadDirectory('');
                      }}
                      sx={{ mt: 1 }}
                    >
                      Cambiar carpeta
                    </Button>
                  </Box>

                  {/* Upload zone */}
                  <Box
                    sx={{
                      border: '2px dashed #667eea',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: '#f8f9ff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: '#f0f2ff',
                        borderColor: '#764ba2'
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      style={{ display: 'none' }}
                      id="audio-upload-input"
                    />
                    <label htmlFor="audio-upload-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                      <CloudUpload sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                      <Typography variant="body1" fontWeight="500" sx={{ color: '#667eea' }}>
                        Click para seleccionar archivo
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Formatos soportados: WAV, MP3, GSM, OGG
                      </Typography>
                    </label>
                  </Box>

                  {uploadFile && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Archivo seleccionado:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AudioFile sx={{ color: '#667eea', mr: 1 }} />
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {uploadFile.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {(uploadFile.size / 1024).toFixed(2)} KB
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={uploading} sx={{ color: '#666' }}>
            Cancelar
          </Button>
          {tab === 1 && (
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!uploadFile || uploading}
              startIcon={<Upload />}
              sx={{
                background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653a8a 100%)',
                },
                '&:disabled': {
                  background: '#e0e0e0',
                  color: '#999'
                }
              }}
            >
              {uploading ? 'Subiendo...' : 'Subir Audio'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
